import api from '../../services/api';
import { ResponseData } from '../../types/response/ResponseData';
import {
    CompanyAddress,
    CompanyLookup,
    CompanyLookupRequest,
    SignupRequest,
} from '../../types/merchantSignup/MerchantSignup';
import { CompanyLookupError, SignupError } from './merchantSignupErrors';

/**
 * The public merchant signup endpoints of merchanthub-api. Both are called without a signed-in user, so neither sends
 * a bearer token, and neither is retried: a lookup is a paid call to the company register and a signup must not be
 * sent twice. Both are behind the TURBO-24-merchant-signup flag in the API and answer 404 while it is off, and both
 * are rate limited (429).
 */

export const SIGNUP_PATH = 'merchant-signup';

export const companyLookupPath = ({ country, organizationNumber }: CompanyLookupRequest) =>
    `merchant-signup/companies/${country}/${encodeURIComponent(organizationNumber)}`;

/**
 * The company as merchanthub-api returns it; every field but the name can be missing in the register. Outside
 * production every field, the name too, is null for a company the register does not know.
 */
interface CompanyLookupResponse {
    name: string | null;
    address: CompanyAddress | null;
    website: string | null;
    phone: string | null;
    email: string | null;
}

/** The signup as merchanthub-api accepts it: queued for the organization, merchant and invite to be created. */
interface SignupResponse {
    signupId: string;
}

/**
 * Looks the company up by country and organization number (GET merchant-signup/companies/{country}/{number}). Rejects
 * with a ResponseError: 404 when no company is registered under the number, 422 when the number is not valid for the
 * country, 502 when the register is unavailable.
 */
export const liveLookupCompany = async (request: CompanyLookupRequest): Promise<CompanyLookup> => {
    const { data } = await api.get<ResponseData<CompanyLookupResponse>>(
        companyLookupPath(request),
        undefined,
        undefined,
        0
    );

    return {
        companyName: data.name?.trim() || null,
        address: data.address ?? null,
        website: data.website ?? null,
        phone: data.phone ?? null,
        email: data.email ?? null,
    };
};

/**
 * Submits the signup (POST merchant-signup). Resolves on 202 with the id of the queued signup; rejects with a
 * ResponseError carrying field errors (e.g. Company.Address.PostalCode, HubUser.Email) on 422 REQUEST_INVALID, or the
 * code of why the company was not accepted on another 422.
 */
export const liveSubmitSignup = async (request: SignupRequest): Promise<string> => {
    const { data } = await api.postWithResponseType<SignupRequest, ResponseData<SignupResponse>>(
        SIGNUP_PATH,
        request,
        undefined,
        { retries: 0 }
    );

    return data.signupId;
};

/** Looks the company up; rejects with a CompanyLookupError. */
export const lookupCompany = (request: CompanyLookupRequest): Promise<CompanyLookup> =>
    liveLookupCompany(request).catch(error => {
        throw CompanyLookupError.from(error);
    });

/** Submits the signup and resolves with its id; rejects with a SignupError. */
export const submitSignup = (request: SignupRequest): Promise<string> =>
    liveSubmitSignup(request).catch(error => {
        throw SignupError.from(error);
    });
